'use client';

/**
 * GlobalReauthHandler
 *
 * App-wide listener for the `auth:circuit-breaker` event dispatched by the
 * Supabase browser client (lib/supabase/client.ts) when its circuit-breaker
 * trips on a terminal token failure.
 *
 * Without this, ONLY the active-call page (Conference.tsx) and the incident-
 * report page mount a ReauthModal — every other page (admin panel, deep
 * analysis, reports, dashboard, etc.) would force-redirect to /login on a
 * mid-task token expiry, losing all in-flight work. This component shows the
 * same in-place ReauthModal app-wide so the user can re-authenticate without
 * losing context.
 *
 * Mounted once in app/template.tsx, rendered on every route except those
 * explicitly bypassed by template.tsx.
 */

import { useEffect, useState } from 'react';
import { ReauthModal } from '@/components/conference/ReauthModal';
import logger from '@/lib/logger';

export function GlobalReauthHandler() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onCircuitBreaker(ev: Event) {
      const detail = (ev as CustomEvent).detail;
      logger.warn('GlobalReauthHandler', 'Circuit-breaker event received', { detail });
      setOpen(true);
    }
    window.addEventListener('auth:circuit-breaker', onCircuitBreaker as EventListener);
    return () => {
      window.removeEventListener('auth:circuit-breaker', onCircuitBreaker as EventListener);
    };
  }, []);

  if (!open) return null;

  return (
    <ReauthModal
      isOpen={open}
      onClose={() => setOpen(false)}
      onSuccess={() => setOpen(false)}
    />
  );
}
